import { Lang } from "../context/LanguageContext";

export interface EventMeta {
  dateRange: string;
  location: string;
  duration: string;
  language: string;
}

export interface ProgramSession {
  time: string;
  title: string;
}

export interface ProgramDay {
  dayLabel: string;
  date: string;
  weekday: string;
  title: string;
  timeRange: string;
  sessions: ProgramSession[];
  dubaiExperience: string;
}

export interface Instructor {
  name: string;
  credentials: string;
  bio: string[];
  tags: string[];
}

export interface Product {
  name: string;
  shortName: string;
  topics: string[];
  price: number;
  originalPrice?: number;
  currency: string;
  priceNote: string;
  seatsNote: string;
  depositAmount: number;
  remainingAmount: number;
  installmentOptions: number[];
  heroImage: string;
  galleryImages: string[];
  summary: string;
  highlights: string[];
  badges: string[];
  audience: string[];
  eventMeta: EventMeta;
  aboutTitle: string;
  aboutParagraphs: string[];
  aboutImage: string;
  trainingCenter: string;
  programDays: ProgramDay[];
  instructor: Instructor;
  faq: { question: string; answer: string }[];
}

// Placeholder (indicative) price - will be updated once final pricing is confirmed.
const productEn: Product = {
  name: "Clinical Veterinary Dentistry",
  shortName: "Clinical Veterinary Dentistry Program",
  topics: ["Theory", "Dental Radiography", "Tooth Extractions", "Surgical Techniques"],
  price: 150000,
  currency: "TRY",
  priceNote: "Price shown is indicative; final pricing will be confirmed prior to registration.",
  seatsNote: "Limited seats · Hands-on training in small groups",
  depositAmount: 30000,
  remainingAmount: 120000,
  installmentOptions: [1, 2, 3, 6, 9],
  heroImage: "/yen.jpeg",
  galleryImages: ["/yen.jpeg"],
  summary:
    "An intensive, fully hands-on training program on fresh cadavers, held in Dubai and designed to develop your surgical and anatomical skills on real tissue.",
  highlights: [
    "Hands-on training in small groups with one-on-one mentorship",
    "Practice on fresh cadavers with a realistic tissue feel",
    "Step-by-step guidance from expert instructors",
    "Certificate of participation upon completion",
  ],
  badges: [
    "Certified Program",
    "Small Group / One-on-One Mentorship",
    "Limited Seats",
    "Expert Instructor Team",
  ],
  audience: [
    "Veterinarians and veterinary medicine students",
    "Clinicians looking to strengthen their surgical skills on real tissue",
    "Participants seeking advanced practice for academic or research purposes",
  ],
  eventMeta: {
    dateRange: "Oct 28 – Nov 1, 2026",
    location: "Dubai, UAE",
    duration: "4 days training + 1 day technical visit",
    language: "English · Turkish support available",
  },
  aboutTitle: "Turn knowledge into real hands-on experience.",
  aboutParagraphs: [
    "Held in Dubai by Haptone, this program combines theoretical training, practice on 3D models, hands-on fresh cadaver wet lab sessions, and technical visits to veterinary healthcare facilities across the United Arab Emirates.",
    "Participants apply surgical techniques step by step alongside an experienced instructor, aiming to build a more systematic clinical approach to case assessment, surgical planning, proper instrument use, and tissue-appropriate technique.",
  ],
  aboutImage: "/images/kk.png",
  trainingCenter: "Nova Centre of Excellence, Dubai",
  programDays: [
    {
      dayLabel: "Day 1",
      date: "Oct 28",
      weekday: "Wednesday",
      title: "Theoretical Training",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:35", title: "Registration, welcome and program opening" },
        { time: "09:35–10:15", title: "Oral examination and treatment planning" },
        { time: "10:30–11:50", title: "Dental anatomy and intraoral radiography" },
        { time: "12:35–13:20", title: "Radiographic positioning and interpretation" },
        { time: "13:35–14:15", title: "Extraction instruments and basic principles" },
        { time: "14:15–16:00", title: "Surgical planning and case assessment" },
      ],
      dubaiExperience: "Dubai Mall and Burj Khalifa area",
    },
    {
      dayLabel: "Day 2",
      date: "Oct 29",
      weekday: "Thursday",
      title: "Oral Examination & Dental Radiography",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:20", title: "Wet lab orientation" },
        { time: "09:20–10:50", title: "Oral examination, charting and planning" },
        { time: "11:05–11:50", title: "Radiographic positioning in dogs" },
        { time: "12:35–13:20", title: "Radiographic positioning in cats" },
        { time: "13:35–14:25", title: "Full-mouth dental radiography" },
        { time: "14:25–16:00", title: "Interpretation, troubleshooting and feedback" },
      ],
      dubaiExperience: "Dubai Marina, JBR Walk and Bluewaters",
    },
    {
      dayLabel: "Day 3",
      date: "Oct 30",
      weekday: "Friday",
      title: "Tooth Extractions in Cats & Dogs",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:20", title: "Instructor demonstration and instrument overview" },
        { time: "09:20–10:10", title: "Single-rooted tooth extractions in cats" },
        { time: "10:25–11:20", title: "Simple tooth extractions in dogs" },
        { time: "12:05–12:55", title: "Extraction of two-rooted teeth" },
        { time: "13:10–14:00", title: "Extraction of three-rooted teeth" },
        { time: "14:00–16:00", title: "Flap closure, suturing and radiographic control" },
      ],
      dubaiExperience: "Al Seef, Dubai Creek and Al Fahidi",
    },
    {
      dayLabel: "Day 4",
      date: "Oct 31",
      weekday: "Saturday",
      title: "Advanced Techniques & Complication Management",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:20", title: "Advanced extraction planning" },
        { time: "09:20–10:10", title: "Maxillary canine tooth extraction" },
        { time: "10:25–11:20", title: "Mandibular canine and difficult extractions" },
        { time: "12:05–12:50", title: "Retained and fractured roots" },
        { time: "12:50–13:35", title: "Managing complications" },
        { time: "13:50–16:00", title: "Advanced scenarios, assessment and certification" },
      ],
      dubaiExperience: "Desert safari and evening program",
    },
    {
      dayLabel: "Day 5",
      date: "Nov 1",
      weekday: "Sunday",
      title: "Technical Visits",
      timeRange: "08:00–19:00",
      sessions: [
        { time: "08:00", title: "Departure from hotel" },
        { time: "08:30–10:30", title: "Equine & Camel Hospital" },
        { time: "10:30–12:00", title: "Transfer to Abu Dhabi" },
        { time: "12:00–13:00", title: "Lunch" },
        { time: "13:30–15:30", title: "Abu Dhabi Falcon Hospital" },
        { time: "16:00–19:00", title: "Abu Dhabi city program and return to Dubai" },
      ],
      dubaiExperience: "Visits are subject to the availability and confirmation of the host institutions.",
    },
  ],
  instructor: {
    name: "Dr. Adrienn Fergelt",
    credentials: "DVM · Dentistry & Oral Surgery · ESAVS, MASVM",
    bio: [
      "A graduate of the University of Veterinary Medicine Budapest, Dr. Fergelt has over 12 years of clinical experience in small animal practice and nearly 8 years of intensive work in veterinary dentistry.",
      "Based in Abu Dhabi, she practices veterinary dentistry and oral surgery, conducting advanced clinical work in dental radiography, simple and surgical tooth extractions, periodontal surgery, restorative and endodontic procedures, and oral and maxillofacial surgery.",
    ],
    tags: ["Dental radiography", "Surgical extractions", "Periodontal surgery", "Oral surgery"],
  },
  faq: [
    {
      question: "Which level of participants is the program suitable for?",
      answer:
        "The program is designed for veterinary professionals, veterinary students, and clinicians with a foundational background in veterinary medicine.",
    },
    {
      question: "Are seats limited?",
      answer:
        "Yes, the number of participants is kept limited to ensure the effectiveness of the hands-on training.",
    },
    {
      question: "Is a certificate of participation provided?",
      answer:
        "Yes, participants who successfully complete the program receive a certificate of participation.",
    },
  ],
};

const productTr: Product = {
  name: "Klinik Veteriner Diş Hekimliği",
  shortName: "Klinik Veteriner Diş Hekimliği Programı",
  topics: ["Teori", "Dental Radyografi", "Diş Çekimi", "Cerrahi Teknikler"],
  price: 150000,
  currency: "TRY",
  priceNote: "Gösterilen fiyat bilgilendirme amaçlıdır; kesin fiyat kayıt öncesinde teyit edilecektir.",
  seatsNote: "Sınırlı kontenjan · Küçük gruplarla uygulamalı eğitim",
  depositAmount: 30000,
  remainingAmount: 120000,
  installmentOptions: [1, 2, 3, 6, 9],
  heroImage: "/ytr.jpeg",
  galleryImages: ["/ytr.jpeg"],
  summary:
    "Dubai'de gerçekleştirilen, tamamen uygulamalı ve taze kadavra üzerinde yapılan; cerrahi ve anatomik becerilerinizi gerçek doku üzerinde geliştirmenizi sağlayan yoğunlaştırılmış bir eğitim programı.",
  highlights: [
    "Küçük gruplarla, birebir mentorluk eşliğinde uygulamalı eğitim",
    "Gerçekçi doku hissi veren taze kadavralar üzerinde uygulama",
    "Uzman eğitmenlerden adım adım rehberlik",
    "Program sonunda katılım sertifikası",
  ],
  badges: [
    "Sertifikalı Program",
    "Küçük Grup / Birebir Mentorluk",
    "Sınırlı Kontenjan",
    "Uzman Eğitmen Kadrosu",
  ],
  audience: [
    "Veteriner hekimler ve veteriner fakültesi öğrencileri",
    "Gerçek doku üzerinde cerrahi becerilerini geliştirmek isteyen klinisyenler",
    "Akademik veya araştırma amaçlı ileri düzey uygulama arayan katılımcılar",
  ],
  eventMeta: {
    dateRange: "28 Ekim – 1 Kasım 2026",
    location: "Dubai, BAE",
    duration: "4 gün eğitim + 1 gün teknik gezi",
    language: "İngilizce · Türkçe destek mevcuttur",
  },
  aboutTitle: "Bilgiyi gerçek, uygulamalı deneyime dönüştürün.",
  aboutParagraphs: [
    "Haptone tarafından Dubai'de düzenlenen bu program; teorik eğitim, 3D modeller üzerinde pratik, uygulamalı taze kadavra laboratuvar oturumları ve Birleşik Arap Emirlikleri genelindeki veteriner sağlık tesislerine teknik gezileri bir araya getirir.",
    "Katılımcılar, deneyimli bir eğitmen eşliğinde cerrahi teknikleri adım adım uygulayarak; vaka değerlendirmesi, cerrahi planlama, doğru enstrüman kullanımı ve doku uyumlu teknik konularında daha sistematik bir klinik yaklaşım geliştirmeyi hedefler.",
  ],
  aboutImage: "/images/kk.png",
  trainingCenter: "Nova Centre of Excellence, Dubai",
  programDays: [
    {
      dayLabel: "1. Gün",
      date: "28 Ekim",
      weekday: "Çarşamba",
      title: "Teorik Eğitim",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:35", title: "Kayıt, karşılama ve program açılışı" },
        { time: "09:35–10:15", title: "Ağız içi muayene ve tedavi planlaması" },
        { time: "10:30–11:50", title: "Dental anatomi ve ağız içi radyografi" },
        { time: "12:35–13:20", title: "Radyografik pozisyonlama ve yorumlama" },
        { time: "13:35–14:15", title: "Çekim enstrümanları ve temel prensipler" },
        { time: "14:15–16:00", title: "Cerrahi planlama ve vaka değerlendirmesi" },
      ],
      dubaiExperience: "Dubai Mall ve Burj Khalifa bölgesi",
    },
    {
      dayLabel: "2. Gün",
      date: "29 Ekim",
      weekday: "Perşembe",
      title: "Ağız Muayenesi & Dental Radyografi",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:20", title: "Laboratuvar oryantasyonu" },
        { time: "09:20–10:50", title: "Ağız muayenesi, kayıt ve planlama" },
        { time: "11:05–11:50", title: "Köpeklerde radyografik pozisyonlama" },
        { time: "12:35–13:20", title: "Kedilerde radyografik pozisyonlama" },
        { time: "13:35–14:25", title: "Tam ağız dental radyografi" },
        { time: "14:25–16:00", title: "Yorumlama, sorun giderme ve geri bildirim" },
      ],
      dubaiExperience: "Dubai Marina, JBR Walk ve Bluewaters",
    },
    {
      dayLabel: "3. Gün",
      date: "30 Ekim",
      weekday: "Cuma",
      title: "Kedi ve Köpeklerde Diş Çekimi",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:20", title: "Eğitmen gösterimi ve enstrüman tanıtımı" },
        { time: "09:20–10:10", title: "Kedilerde tek köklü diş çekimi" },
        { time: "10:25–11:20", title: "Köpeklerde basit diş çekimi" },
        { time: "12:05–12:55", title: "İki köklü dişlerin çekimi" },
        { time: "13:10–14:00", title: "Üç köklü dişlerin çekimi" },
        { time: "14:00–16:00", title: "Flep kapama, sütür ve radyografik kontrol" },
      ],
      dubaiExperience: "Al Seef, Dubai Creek ve Al Fahidi",
    },
    {
      dayLabel: "4. Gün",
      date: "31 Ekim",
      weekday: "Cumartesi",
      title: "İleri Teknikler & Komplikasyon Yönetimi",
      timeRange: "09:00–16:00",
      sessions: [
        { time: "09:00–09:20", title: "İleri düzey çekim planlaması" },
        { time: "09:20–10:10", title: "Üst çene köpek dişi çekimi" },
        { time: "10:25–11:20", title: "Alt çene köpek dişi ve zor çekimler" },
        { time: "12:05–12:50", title: "Kalan ve kırık kökler" },
        { time: "12:50–13:35", title: "Komplikasyon yönetimi" },
        { time: "13:50–16:00", title: "İleri senaryolar, değerlendirme ve sertifikasyon" },
      ],
      dubaiExperience: "Çöl safarisi ve akşam programı",
    },
    {
      dayLabel: "5. Gün",
      date: "1 Kasım",
      weekday: "Pazar",
      title: "Teknik Ziyaretler",
      timeRange: "08:00–19:00",
      sessions: [
        { time: "08:00", title: "Otelden hareket" },
        { time: "08:30–10:30", title: "At ve Deve Hastanesi" },
        { time: "10:30–12:00", title: "Abu Dabi'ye transfer" },
        { time: "12:00–13:00", title: "Öğle yemeği" },
        { time: "13:30–15:30", title: "Abu Dabi Şahin Hastanesi" },
        { time: "16:00–19:00", title: "Abu Dabi şehir programı ve Dubai'ye dönüş" },
      ],
      dubaiExperience: "Ziyaretler, ilgili kurumların uygunluğu ve onayına bağlıdır.",
    },
  ],
  instructor: {
    name: "Dr. Adrienn Fergelt",
    credentials: "DVM · Diş Hekimliği ve Ağız Cerrahisi · ESAVS, MASVM",
    bio: [
      "Budapeşte Veteriner Tıp Üniversitesi mezunu olan Dr. Fergelt, küçük hayvan pratiğinde 12 yılı aşkın klinik deneyime ve veteriner diş hekimliğinde yaklaşık 8 yıllık yoğun çalışma geçmişine sahiptir.",
      "Abu Dabi merkezli olarak veteriner diş hekimliği ve ağız cerrahisi alanında çalışmalarını sürdürmekte; dental radyografi, basit ve cerrahi diş çekimleri, periodontal cerrahi, restoratif ve endodontik işlemler ile ağız ve çene-yüz cerrahisi konularında ileri düzey klinik çalışmalar yürütmektedir.",
    ],
    tags: ["Dental radyografi", "Cerrahi çekimler", "Periodontal cerrahi", "Ağız cerrahisi"],
  },
  faq: [
    {
      question: "Program hangi seviyedeki katılımcılar için uygundur?",
      answer:
        "Program; veteriner hekimlik, veterinerlik öğrencileri ve veteriner tıp alanında temel bilgiye sahip klinisyenler için tasarlanmıştır.",
    },
    {
      question: "Kontenjan sınırlı mı?",
      answer:
        "Evet, uygulamalı eğitimin etkinliğini sağlamak amacıyla katılımcı sayısı sınırlı tutulmaktadır.",
    },
    {
      question: "Katılım sertifikası veriliyor mu?",
      answer: "Evet, programı başarıyla tamamlayan katılımcılara katılım sertifikası verilmektedir.",
    },
  ],
};

export const productsByLang: Record<Lang, Product> = {
  en: productEn,
  tr: productTr,
};

export function getProduct(lang: Lang): Product {
  return productsByLang[lang];
}
